import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useBi } from "../lib/customer";
import { useSite } from "../lib/site";
import { fmtSAR } from "../lib/format";

export function NLSearch({ className = "", testid = "nl-search" }) {
  const b = useBi();
  const { lang } = useI18n();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await api.post("/ai/search", { q: q.trim(), lang });
      const p = new URLSearchParams(Object.entries(r.data.filters).map(([k, v]) => [k, String(v)]));
      p.set("nlq", q.trim());
      p.set("nl", r.data.source);
      nav(`/properties?${p}`);
    } catch {
      toast.error(b("Smart search unavailable — using keyword search.", "البحث الذكي غير متاح — سيتم البحث بالكلمات."));
      nav(`/properties?q=${encodeURIComponent(q.trim())}`);
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={go} className={`flex gap-2 ${className}`} data-testid={testid} role="search">
      <div className="relative flex-1">
        <Sparkles className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cta" />
        <input value={q} onChange={(e) => setQ(e.target.value)} dir="auto" maxLength={300} aria-label={b("Smart search", "البحث الذكي")} data-testid={`${testid}-input`}
          placeholder={b("Try: luxury villa in Riyadh under SAR 2 million", "جرّب: فيلا فاخرة في الرياض بأقل من مليوني ريال")} className="field !ps-10 !py-3" />
      </div>
      <button type="submit" disabled={busy} data-testid={`${testid}-submit`} className="btn-cta !rounded-sm !px-5 !py-3">{busy ? "…" : b("Ask AI", "ابحث")}</button>
    </form>
  );
}

const KEYS = ["q", "city", "neighborhood", "type", "purpose", "min_price", "max_price", "bedrooms", "bathrooms", "min_size", "max_size", "completion", "furnishing", "availability", "amenities", "parking", "tag"];

export function FilterChips() {
  const b = useBi();
  const { lang } = useI18n();
  const { label, cityName, nbName } = useSite();
  const [params, setParams] = useSearchParams();
  const active = KEYS.filter((k) => params.get(k));
  if (!active.length) return null;
  const txt = (k, v) => {
    if (k === "city") return cityName(v);
    if (k === "neighborhood") return nbName(params.get("city"), v);
    if (k === "type") return v.split(",").map((x) => label("property_type", x)).join(", ");
    if (k === "min_price") return `≥ ${fmtSAR(Number(v), lang)}`;
    if (k === "max_price") return `≤ ${fmtSAR(Number(v), lang)}`;
    if (k === "bedrooms") return b(`${v}+ beds`, `${v}+ غرف`);
    if (k === "bathrooms") return b(`${v}+ baths`, `${v}+ دورات مياه`);
    if (k === "q") return `“${v}”`;
    return `${k.replace("_", " ")}: ${v}`;
  };
  const remove = (k) => { const p = new URLSearchParams(params); p.delete(k); p.delete("page"); if (k === "city") p.delete("neighborhood"); setParams(p); };
  return (
    <div className="mb-6" data-testid="filter-chips">
      {params.get("nlq") && <p className="mb-2 text-xs text-fg/55" data-testid="nl-interpreted">{b("Interpreted from", "تم تفسير")} “{params.get("nlq")}” {params.get("nl") === "ai" ? b("by AI", "بالذكاء الاصطناعي") : b("(keyword fallback)", "(بحث بالكلمات)")} — {b("edit below:", "يمكنك التعديل:")}</p>}
      <div className="flex flex-wrap gap-2">
        {active.map((k) => (
          <button key={k} type="button" onClick={() => remove(k)} data-testid={`filter-chip-${k}`} className="chip chip-on !py-1">
            {txt(k, params.get(k))}<X className="h-3 w-3" aria-label={b("Remove", "إزالة")} />
          </button>
        ))}
      </div>
    </div>
  );
}
